/**
 * /sign-in — dedicated auth route.
 *
 * Renders SignInGate. On session established, navigates to `returnTo` when a
 * safe same-origin `/app…` path is present (used by the group invite flow to
 * bring a signed-out visitor back to `/app/join/<token>`), else `/app`.
 *
 * This route exists so sign-out can redirect to a URL that contains "sign-in"
 * rather than "/" (which the root index immediately redirects back to /app).
 * The e2e golden-path step 8 asserts `toHaveURL(/sign-in|login|\/$/)`.
 *
 * Design refs: design.md §2, §4 — same visual constraints as SignInGate.
 * Voice: branding.md §5 — quiet, unsentimental, no exclamations.
 * CLAUDE.md Rule 7 — SignInGate is already verified at 375px / 768px / 1280px.
 */

import { useNavigate, useSearchParams } from "react-router";
import { useCallback } from "react";
import { SignInGate } from "~/features/auth/SignInGate";
import { resolvePostAuthRedirect } from "~/features/groups/pendingGroupJoin";
import { resolvePendingFriendInviteRedirect } from "~/features/friends/pendingFriendInvite";
import "@proof/ui/tokens";

/**
 * Only accept an in-app or public return path. Anything that isn't a bare
 * `/app…`, `/r/…`, `/u/…`, `/g/…`, or `/groups…` path (no scheme, no `//`
 * protocol-relative, no back-navigation) falls back to `/app` — an open-redirect
 * guard.
 *
 * B-WP8a (D-8.4): the Report affordance on public `/r/` and `/u/` pages sends a
 * signed-out viewer to `/sign-in?returnTo=<path>`, so those two prefixes join
 * the allow-list. B-WP12: the public groups directory (`/groups`) and public
 * group page (`/g/:groupUuid`) send a signed-out join / report tap through the
 * same returnTo, so a deep link survives sign-in. Same shape: path-only, no
 * scheme, no `//`.
 */
export function safeReturnTo(raw: string | null): string {
  if (!raw) return "/app";
  // Reject protocol-relative (`//host`) FIRST — before any prefix match.
  if (raw.startsWith("//")) return "/app";
  if (
    raw.startsWith("/app") ||
    raw.startsWith("/r/") ||
    raw.startsWith("/u/") ||
    raw.startsWith("/g/") ||
    raw.startsWith("/groups")
  ) {
    return raw;
  }
  return "/app";
}

export default function SignInRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get("returnTo"));

  const handleSessionEstablished = useCallback(() => {
    // A pending group invite (signup-via-group-invite path) takes precedence
    // over returnTo and routes to the join page with auto-file (operator Q1).
    // Else, a pending FRIEND invite (B-WP11c) routes to the friend-invite landing.
    const afterGroup = resolvePostAuthRedirect(returnTo);
    const dest =
      afterGroup === returnTo ? resolvePendingFriendInviteRedirect(returnTo) : afterGroup;
    void navigate(dest, { replace: true });
  }, [navigate, returnTo]);

  return <SignInGate onSessionEstablished={handleSessionEstablished} />;
}
